import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Spinner,
  Table,
  Pagination
} from 'react-bootstrap';
import axiosInstance from '../../../AxiosInstance.tsx';
import { API_ENDPOINTS } from '../../utils.ts';
import ShortMain from "../../../images/short-icon-main.svg"
import ShortUp from "../../../images/short-icon-top.svg"
import ShortDown from "../../../images/short-icon-bottom.svg"
import "../../../App.css"
interface PaymentInfoTabProps {
  userId: string;
  isActive: boolean;
}

interface PaymentData {
  transactionId: string;
  date: string;
  amount: number;
  stripeFeePercentage: number;
  stripeFixedFee: number;
  totalStripeFee: number;
  netAmount: number;
  status: string;
  paymentType: string;
  cardBrand: string;
  cardLast4: string;
  type: string;
}

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

const PaymentInfoTab: React.FC<PaymentInfoTabProps> = ({ userId, isActive }) => {
  // Payment Information Table States
  const [paymentData, setPaymentData] = useState<PaymentData[]>([]);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });

  const ITEMS_PER_PAGE = 10;

  // Use useCallback to memoize the fetchPaymentData function
  const fetchPaymentData = useCallback(async (
    id: string,
    page: number = 1,
    sortField: string = '',
    sortDirection: string = ''
  ) => {
    try {
      setPaymentLoading(true);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        toast.error('Authentication required. Please login again.');
        return;
      }

      // Prepare request parameters
      const params: any = {
        page,
        limit: ITEMS_PER_PAGE
      };

      // Add sorting parameters
      if (sortField && sortDirection) {
        params.sortBy = sortField;
        params.sortOrder = sortDirection;
      }

      const response = await axiosInstance.get(
        `${API_ENDPOINTS.GET_USER_PAYMENT_HISTORY}/${id}`,
        {
          params,
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
          },
        }
      );

      if (response.data.status === "Success") {
        const payments = response.data.data || [];
        const paginationData = response.data.pagination || {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        };

        setPaymentData(payments);
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);
      } else {
        toast.error(response.data.message || 'Failed to fetch payment data');
        setPaymentData([]);
        setPagination({
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch payment data');
      setPaymentData([]);
      setPagination({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1
      });
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  // Fetch payment data when Payment Information tab is active
  useEffect(() => {
    if (isActive && userId) {
      fetchPaymentData(userId, currentPage, sortConfig.sortBy, sortConfig.sortOrder);
    }
  }, [isActive, userId, currentPage, sortConfig.sortBy, sortConfig.sortOrder, fetchPaymentData]);

  // Handle sorting - Fixed to prevent unwanted API calls
  const handleSort = useCallback((field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        // Reset sorting - no sort
        setSortConfig({ sortBy: '', sortOrder: '' });
        setCurrentPage(1);
        fetchPaymentData(userId, 1);
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    setCurrentPage(1);
    fetchPaymentData(userId, 1, field, newSortOrder);
  }, [sortConfig.sortBy, sortConfig.sortOrder, userId, fetchPaymentData]);

  // Get sort icon for sortable columns - Fixed styling
  const getSortIcon = useCallback((field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>  
      );
    }
    
    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  }, [sortConfig.sortBy, sortConfig.sortOrder]);

  // Handle pagination - updated to work with backend pagination
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page);
      fetchPaymentData(userId, page, sortConfig.sortBy, sortConfig.sortOrder);
    }
  }, [pagination.totalPages, currentPage, userId, sortConfig.sortBy, sortConfig.sortOrder, fetchPaymentData]);

  // Format date helper function
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format amount helper function
  const formatAmount = (amount: number) => {
    if (!amount && amount !== 0) return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  // Format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${dateStr} ${timeStr}`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format card type
  const formatCardType = (cardBrand: string, cardLast4: string) => {
    if (!cardBrand && !cardLast4) return 'N/A';
    const brand = cardBrand ? cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) : '';
    return `${brand} **** ${cardLast4}`;
  };

  // Get payment status badge helper function
  const getPaymentStatusBadge = (status: string) => {
  const statusLower = status?.toLowerCase() || '';
  let badgeClass = 'round-badge badge bg-secondary';
  let displayText = status;

  // Map backend status to display text
  switch (statusLower) {
    case 'active':
      displayText = 'Paid';
      badgeClass = 'round-badge badge bg-success';
      break;
    case 'inactive':
      displayText = 'Pending';
      badgeClass = 'round-badge badge bg-warning text-dark';
      break;
    case 'suspended':
      displayText = 'Failed';
      badgeClass = 'round-badge badge bg-danger';
      break;
    case 'scheduled':
      displayText = 'Scheduled';
      badgeClass = 'round-badge badge bg-or-badge';
      break;
    // Keep existing mappings for other possible statuses
    case 'paid':
    case 'success':
    case 'completed':
      displayText = 'Paid';
      badgeClass = 'round-badge badge bg-success';
      break;
    case 'failed':
    case 'error':
    case 'declined':
      displayText = 'Failed';
      badgeClass = 'round-badge badge bg-danger';
      break;
    case 'pending':
    case 'processing':
      displayText = 'Pending';
      badgeClass = 'round-badge badge bg-warning text-dark';
      break;
    default:
      displayText = status || 'Unknown';
      badgeClass = 'round-badge badge bg-secondary';
  }

  return (
    <span className={badgeClass}>{displayText}</span>
  );
};

  return (
    <>
      {/* Add Bootstrap Icons CSS if not already included */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css"
        rel="stylesheet"
      />

      <div className="tab-pane-content payments-tab">
        <div className="main-table">
          <div className="table-responsive mt-3">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th
                    onClick={() => handleSort("transactionId")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      Transaction ID {getSortIcon("transactionId")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("type")}
                    style={{ cursor: "pointer" }}
                  >
                    <span
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      Type {getSortIcon("type")}
                    </span>
                  </th>
                  <th>Date & Time</th>
                  <th>Amount</th>
                  <th>Card Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading payment data...</p>
                    </td>
                  </tr>
                ) : paymentData.length > 0 ? (
                  paymentData.map((payment, index) => (
                    <tr key={payment.transactionId || index}>
                      <td>{payment?.transactionId || "N/A"}</td>
                      <td style={{ textTransform: 'capitalize' }}>{payment?.type || "N/A"}</td>
                      <td>{formatDateTime(payment?.date)}</td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {formatAmount(payment?.amount)}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                            Amount: {formatAmount(payment?.amount)} + Stripe fee: {formatAmount(payment?.stripeFeePercentage)} + Fixed fee: {formatAmount(payment?.stripeFixedFee)}
                          </div>
                        </div>
                      </td>
                      <td>{formatCardType(payment?.cardBrand, payment?.cardLast4)}</td>
                      <td>{getPaymentStatusBadge(payment?.status)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <p className="text-muted mb-0">
                        No payment history found
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination for Payment Table */}
        {!paymentLoading &&
          paymentData.length > 0 &&
          pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                Showing {(pagination.currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                {Math.min(
                  pagination.currentPage * ITEMS_PER_PAGE,
                  pagination.totalCount
                )}{" "}
                of {pagination.totalCount} entries
              </div>
              <Pagination>
                <Pagination.Prev 
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                />
                <Pagination.Next 
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                />
              </Pagination>
            </div>
          )}
      </div>
    </>
  );
};

export default PaymentInfoTab;